/**
 * @module components/Dashboard/Dashboard
 * @description Dashboard principal de cumplimiento ISO 27002.
 *
 * Vista de entrada del sistema. Muestra métricas agregadas de todos los controles,
 * gráfico de cumplimiento por categoría, actividad reciente y tabla de controles
 * con filtros y búsqueda.
 *
 * El título del dashboard cambia dinámicamente según el framework activo
 * (ISO 27002, ISO 27001, LMCS, CIS) para reflejar el contexto normativo actual.
 *
 * @see MetricsCards para los KPIs en la parte superior
 * @see ComplianceChart para el gráfico de barras por categoría
 * @see ControlsTable para la tabla de controles principal
 * @see useFramework para el framework normativo activo
 */
import React, { useMemo, useCallback } from 'react';
import { MetricsCards } from './MetricsCards';
import { ComplianceChart } from './ComplianceChart';
import { RecentActivity } from './RecentActivity';
import { ControlsTable } from '../Controls/ControlsTable';
import { SelectedControl } from '../../types/iso';
import { useData } from '../../contexts/DataContext';
import { useFramework } from '../../contexts/FrameworkContext';

interface DashboardProps {
  onSelectControl: (control: SelectedControl | null) => void;
}

export function Dashboard({ onSelectControl }: DashboardProps) {
  const { controls } = useData();
  const { activeFramework } = useFramework();

  // Generar título dinámico según el framework activo
  const getDashboardTitle = () => {
    if (!activeFramework) return 'Dashboard de Cumplimiento';
    return `Dashboard de Cumplimiento ${activeFramework.name}`;
  };

  // Generar subtítulo de controles según framework
  const getControlsTitle = () => {
    if (!activeFramework) return 'Controles';
    return `Controles ${activeFramework.name}`;
  };

  const controlsById = useMemo(
    () => new Map(controls.map(c => [c.id, c])),
    [controls]
  );

  const handleSelectControlFromActivity = useCallback((controlId: string) => {
    const control = controlsById.get(controlId);
    if (control) {
      const breadcrumb: string[] = [];

      if (control.parentId) {
        const parent = controlsById.get(control.parentId);
        if (parent) {
          breadcrumb.push(parent.code, parent.title);
        }
      }

      breadcrumb.push(control.code, control.title);

      const selectedControl: SelectedControl = { ...control, breadcrumb };
      onSelectControl(selectedControl);
    }
  }, [controlsById, onSelectControl]);

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          {getDashboardTitle()}
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Monitoreo integral del estado de controles y cumplimiento normativo
        </p>
      </div>

      <MetricsCards />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <ComplianceChart />
        </div>
        <div>
          <RecentActivity onSelectControl={handleSelectControlFromActivity} />
        </div>
      </div>

      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          {getControlsTitle()}
        </h2>
        <ControlsTable onSelectControl={onSelectControl} />
      </div>
    </div>
  );
}